/*
 * OASIS NATION — Cinematic scroll-scrub hero
 * Paints an all-keyframe reveal video to a canvas, frame index driven by scroll.
 * Desktop: pinned stage, scrubs whole car -> brand reveal with a slow push-in.
 * Touch / reduced-motion / small screens: graceful autoplay loop fallback.
 * Also drives the transparent-over-hero header (solidifies past the hero).
 *
 * Editor-resilient: the theme editor re-renders a section's DOM on every setting
 * change. init() can run again safely — a generation token retires the previous
 * rAF/rVFC loops and old window listeners are removed — so tweaking hero (or any)
 * settings never leaves a frozen canvas or stacks duplicate loops.
 */

/* ============================================================
 * Black intro title card (opt-in via the section's "Show black intro" setting).
 * Renders server-side so it's painted before any JS (no flash); this just holds
 * it on black, locks scroll, then lifts it away into the hero.
 * ============================================================ */
(function () {
  'use strict';
  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function runIntro() {
    var veil = document.querySelector('[data-intro]');
    if (!veil || veil._ran) return;
    veil._ran = true;
    var hero = veil.closest('.oasis-chero') || document.querySelector('.oasis-chero');
    var content = hero && hero.querySelector('.oasis-chero__content');
    var heading = hero && hero.querySelector('.oasis-chero__heading');
    var kicker = hero && hero.querySelector('.oasis-chero__kicker');
    if (!hero || !content || !heading) {
      veil.classList.add('is-done');
      if (hero) hero.classList.remove('intro-hold', 'intro-pose', 'oasis-chero--intro');
      return;
    }

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var delay = parseInt(veil.getAttribute('data-intro-delay'), 10);
    if (isNaN(delay)) delay = 1600;
    if (reduce) delay = 250;

    // Scroll lock. NOTE: html has overflow-x: clip (mobile hardening), which stops
    // body{overflow:hidden} from propagating to the viewport — the lock must go on
    // the html element itself.
    var body = document.body, docEl = document.documentElement;
    var prevOverflow = body.style.overflow;
    var prevHtmlOverflow = docEl.style.overflow;
    body.style.overflow = 'hidden';
    docEl.style.overflow = 'hidden';
    try { window.scrollTo(0, 0); } catch (e) {}
    // motion layer exposes window.oasisLenis; resolve at call time (init order safety)
    var L = function () { return window.oasisLenis || window.lenis; };
    var l0 = L();
    if (l0 && l0.stop) l0.stop();
    // Belt-and-braces input lock: overflow:hidden alone doesn't stop iOS touch
    // scrolling, and wheel deltas would scrub the hero video mid-glide.
    var prevent = function (e) { e.preventDefault(); };
    window.addEventListener('wheel', prevent, { passive: false });
    window.addEventListener('touchmove', prevent, { passive: false });
    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      window.removeEventListener('wheel', prevent);
      window.removeEventListener('touchmove', prevent);
      body.style.overflow = prevOverflow;
      docEl.style.overflow = prevHtmlOverflow;
      var l1 = L();
      if (l1 && l1.start) l1.start();
      try { window.scrollTo(0, 0); } catch (e) {}
    };
    // Hard failsafe: whatever happens, nobody stays locked out of scrolling.
    setTimeout(release, delay + 4500);

    // Pose: scale + recentre the REAL content wrapper so the title reads as the
    // big centered intro card. Inline overrides lift the title/kicker to full
    // strength while posed (their stylesheet state at scroll-0 is dimmed/tilted).
    var posed = false;
    function pose() {
      if (posed) return;
      posed = true;
      var hr = heading.getBoundingClientRect();
      var cr = content.getBoundingClientRect();
      if (!hr.width || !cr.width) { posed = false; return; }
      var cx = cr.left + cr.width / 2, cy = cr.top + cr.height / 2;
      var hx = hr.left + hr.width / 2, hy = hr.top + hr.height / 2;
      var vw = window.innerWidth, vh = window.innerHeight;
      var s = Math.max(1.05, Math.min(vw * 0.86, hr.width * 2.6) / hr.width);
      // place the (scaled) heading's centre at mid-viewport
      var dx = vw / 2 - cx - s * (hx - cx);
      var dy = vh * 0.46 - cy - s * (hy - cy);
      content.style.transition = 'none'; // beat the cursor-tilt transition: pose applies instantly
      content.style.willChange = 'transform';
      content.style.transform = 'translate(-50%, -50%) translate(' + dx + 'px, ' + dy + 'px) scale(' + s + ')';
      content.style.opacity = '1';
      heading.style.opacity = '1';
      heading.style.transform = 'none';
      if (kicker) { kicker.style.opacity = '1'; kicker.style.transform = 'none'; }
      hero.classList.remove('intro-hold');
      hero.classList.add('intro-pose');
    }

    // Fly: ONE transition window in which everything returns to its stylesheet
    // (scroll-driven) state simultaneously — wrapper glides down + shrinks,
    // title/kicker relax to their natural dim/tilt, veil fades out. Ends exactly
    // at the first view; there is nothing to swap afterwards.
    function fly() {
      var ms = reduce ? 0 : 1350;
      var clear = function (el) {
        if (!el) return;
        el.style.opacity = '';
        el.style.transform = '';
      };
      if (!reduce) {
        content.style.transition = 'transform ' + ms + 'ms ' + EASE + ', opacity ' + ms + 'ms ' + EASE;
        heading.style.transition = 'opacity ' + ms + 'ms ' + EASE + ', transform ' + ms + 'ms ' + EASE;
        if (kicker) kicker.style.transition = heading.style.transition;
      }
      // double rAF: make sure the posed state is the committed start frame
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          content.style.animation = 'none'; // release the entrance fade (its end state matches)
          clear(content); clear(heading); clear(kicker);
          veil.classList.add('is-leaving');
          hero.classList.remove('intro-pose'); // hero chrome (cue/frame/meta) eases back in
          // Scroll stays locked until the title has landed: scrolling mid-glide
          // would scrub the video and retarget the motion underneath itself.
          setTimeout(release, ms);
          setTimeout(function () {
            veil.classList.add('is-done');
            hero.classList.remove('oasis-chero--intro');
            [content, heading, kicker].forEach(function (el) {
              if (el) { el.style.transition = ''; el.style.willChange = ''; el.style.animation = ''; }
            });
          }, ms + 150);
        });
      });
    }

    // Sequence. Pose as soon as the display font is real (capped); hold; then
    // fly once the hero footage is ready behind the veil (capped).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(pose).catch(pose);
      setTimeout(pose, 900);
    } else { pose(); }

    setTimeout(function () {
      // Lenis may have initialised after the lock was set; re-stop it.
      var l2 = L();
      if (!released && l2 && l2.stop) l2.stop();
      var video = hero.querySelector('[data-chero-video]');
      var gone = false;
      var go = function () {
        if (gone) return;
        gone = true;
        pose(); // in case the font cap hasn't fired yet
        fly();
      };
      if (video && video.readyState < 2 && !reduce) {
        video.addEventListener('loadeddata', go, { once: true });
        setTimeout(go, 1800);
      } else { go(); }
    }, delay);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runIntro);
  else runIntro();
  // Theme editor: re-run if the hero section (with the intro) is reloaded.
  document.addEventListener('shopify:section:load', function () { setTimeout(runIntro, 40); });

  // Back/forward cache: when the page is restored from bfcache (e.g. the browser
  // back button after visiting a product), the DOM is frozen exactly as it was on
  // leave — possibly mid-intro: black veil up and scroll locked — and the timers
  // that lift it never resume. The visitor has already seen the intro, so force
  // the hero straight to its resolved, unlocked state.
  function finalizeIntro() {
    var hero = document.querySelector('.oasis-chero');
    var veil = document.querySelector('[data-intro]');
    if (veil) { veil.classList.add('is-leaving', 'is-done'); veil._ran = true; }
    if (hero) hero.classList.remove('intro-hold', 'intro-pose', 'oasis-chero--intro');
    // Clear any leftover scroll lock (both html and body — see lock note above).
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    var l = window.oasisLenis || window.lenis;
    if (l && l.start) l.start();
    // Drop any posed inline styles left on the text group.
    ['.oasis-chero__content', '.oasis-chero__heading', '.oasis-chero__kicker'].forEach(function (sel) {
      var el = hero && hero.querySelector(sel);
      if (el) { el.style.opacity = ''; el.style.transform = ''; el.style.transition = ''; el.style.willChange = ''; el.style.animation = ''; }
    });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) finalizeIntro(); });
})();

(function () {
  'use strict';

  var GEN = 0;        // bumped each init; stale loops bail when their gen != GEN
  var prevListeners = null;

  function init() {
    var root = document.querySelector('[data-chero]');
    if (!root) return;
    var body = document.body;
    var stage = root.querySelector('[data-chero-stage]');
    var canvas = root.querySelector('[data-chero-canvas]');
    var video = root.querySelector('[data-chero-video]');
    if (!canvas || !video || !stage) return;

    var myGen = ++GEN;
    function alive() { return myGen === GEN; }

    // Drop window listeners from any previous init so they don't accumulate
    // across editor re-renders.
    if (prevListeners) {
      window.removeEventListener('scroll', prevListeners.scroll);
      window.removeEventListener('resize', prevListeners.resize);
      window.removeEventListener('mousemove', prevListeners.mouse);
    }

    // Transparent header over the whole cinematic intro (hero + reflection);
    // the frosted bar only appears once the content below meets the header.
    body.classList.add('oasis-hero-overlay');
    var headerEl = document.querySelector('.header-wrapper');
    var introEnd = document.querySelector('.oasis-detail') || document.querySelector('.oasis-reflection') || root;
    function updateHeader() {
      var hH = headerEl ? headerEl.offsetHeight : 80;
      if (introEnd.getBoundingClientRect().bottom <= hH) body.classList.add('oasis-hero-scrolled');
      else body.classList.remove('oasis-hero-scrolled');
    }

    var ctx = canvas.getContext('2d', { alpha: false });
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    var small = window.matchMedia('(max-width: 749px)').matches;
    // The reveal finishes BEFORE the scroll ends; the remaining scroll "lingers" on
    // the fully-composed hero (brand + text, gentle breathing) before releasing.
    var endHold = parseFloat(root.getAttribute('data-end-hold'));
    if (isNaN(endHold)) endHold = 0.06;
    var SCRUB_END = Math.max(0.4, 1 - endHold);

    // ---- Fallback: looping background video, static text ----
    if (reduced || coarse || small) {
      root.classList.add('is-fallback');
      var loop = video.getAttribute('data-loop');
      if (loop) { video.src = loop; video.load(); }
      video.loop = true; video.muted = true; video.playsInline = true;
      var tryPlay = function () { video.play().catch(function () {}); };
      video.addEventListener('canplay', tryPlay, { once: true });
      tryPlay();
      root.classList.add('chero-ready');
      // still wire the header behavior on touch
      window.addEventListener('scroll', updateHeader, { passive: true });
      window.addEventListener('resize', updateHeader, { passive: true });
      prevListeners = { scroll: updateHeader, resize: updateHeader, mouse: function () {} };
      updateHeader();
      return;
    }

    // ---- Scrub mode ----
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var dur = 4.0, ready = false, curT = 0, targetT = 0, curZoom = 1.05;
    var mTX = 0, mTY = 0, mX = 0, mY = 0; // cursor parallax, normalized -0.5..0.5
    var phase = 0, bx = 0, by = 0;        // ambient breathing drift (alive when idle)
    var particles = [];

    // Ambient gold motes — count + opacity are editor-adjustable (data attributes).
    var moteCount = parseInt(root.getAttribute('data-motes'), 10);
    if (isNaN(moteCount)) moteCount = 46;
    var moteOp = parseFloat(root.getAttribute('data-motes-op'));
    if (isNaN(moteOp)) moteOp = 1;
    (function initMotes() {
      for (var i = 0; i < moteCount; i++) {
        particles.push({
          x: Math.random(), y: Math.random(),
          r: 0.8 + Math.random() * 2.0,
          spd: 0.0005 + Math.random() * 0.0013,
          swayAmt: 0.0007 + Math.random() * 0.0015,
          swayPh: Math.random() * 6.2832,
          a: 0.08 + Math.random() * 0.22
        });
      }
    })();

    // Live water reflection: a canvas in the section below the hero that mirrors
    // the hero's bottom strip, flipped, every frame — so it's exactly in sync.
    var reflCanvas = document.querySelector('[data-chero-reflection]');
    var reflCtx = reflCanvas ? reflCanvas.getContext('2d', { alpha: false }) : null;

    function resize() {
      var w = stage.clientWidth, h = stage.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      if (reflCanvas) {
        reflCanvas.width = canvas.width;
        reflCanvas.height = Math.max(1, Math.round(reflCanvas.clientHeight * dpr));
      }
      draw();
    }

    function draw() {
      if (!video.videoWidth) return;
      var w = canvas.width, h = canvas.height;
      var s = Math.max(w / video.videoWidth, h / video.videoHeight) * curZoom;
      var dw = video.videoWidth * s, dh = video.videoHeight * s;
      // base centering + cursor parallax + ambient breathing (kept within the cover overflow)
      var ox = (w - dw) / 2 - (mX + bx) * (dw - w) * 0.45;
      var oy = (h - dh) / 2 - (mY + by) * (dh - h) * 0.45;
      ctx.drawImage(video, ox, oy, dw, dh);
      // ambient gold motes drifting up — keeps the hero alive even when scrolling stops
      ctx.fillStyle = '#e2c674';
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        ctx.globalAlpha = p.a * moteOp;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r * dpr, 0, 6.2832);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // mirror the hero's bottom strip into the reflection canvas, flipped
      if (reflCtx && reflCanvas.height) {
        var rh = reflCanvas.height;
        reflCtx.save();
        reflCtx.scale(1, -1);
        reflCtx.drawImage(canvas, 0, h - rh, w, rh, 0, -rh, reflCanvas.width, rh);
        reflCtx.restore();
      }
    }

    // A *paused* video only buffers metadata, leaving the scrubber with no decoded
    // frame to draw (frozen). One muted play->pause kick forces it to buffer frame
    // data, then it stays paused and the scroll drives currentTime.
    video.muted = true; video.playsInline = true;
    var kicked = false;
    function bufferKick() {
      if (kicked) return; kicked = true;
      var pr = video.play();
      if (pr && pr.then) pr.then(function () { video.pause(); }).catch(function () { kicked = false; });
    }

    video.addEventListener('loadedmetadata', function () { dur = video.duration || 4.0; resize(); bufferKick(); });
    video.addEventListener('loadeddata', function () {
      ready = true;
      try { video.currentTime = 0; } catch (e) {}
      root.classList.add('chero-ready');
    });
    video.addEventListener('seeked', draw);
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      var rvfc = function () { if (!alive()) return; draw(); video.requestVideoFrameCallback(rvfc); };
      video.requestVideoFrameCallback(rvfc);
    }
    bufferKick(); // in case metadata is already available

    function progress() {
      var rect = root.getBoundingClientRect();
      var total = root.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      return Math.max(0, Math.min(1, (-rect.top) / total));
    }

    var lastSet = -1;
    function tick() {
      if (!alive()) return;
      var p = progress();
      var t = SCRUB_END > 0 ? Math.min(1, p / SCRUB_END) : 1;
      // Linear scrub: the video advances uniformly with scroll so it feels naturally
      // tied to the page (no fast-middle/slow-end that reads as stalling). The curT
      // lerp below smooths it frame to frame.
      var e = t;
      targetT = e * (dur - 0.05);
      curT += (targetT - curT) * 0.32;
      // ambient drift + breathing zoom so the frozen frame never sits perfectly still
      phase += 0.016;
      bx = Math.sin(phase * 0.27) * 0.35;
      by = Math.cos(phase * 0.21) * 0.30;
      curZoom = 1.05 + e * 0.04 + Math.sin(phase * 0.5) * 0.006;
      mX += (mTX - mX) * 0.06;
      mY += (mTY - mY) * 0.06;
      // advance the motes
      for (var i = 0; i < particles.length; i++) {
        var pt = particles[i];
        pt.y -= pt.spd;
        pt.x += Math.sin(phase + pt.swayPh) * pt.swayAmt;
        if (pt.y < -0.05) { pt.y = 1.05; pt.x = Math.random(); }
      }
      if (ready && Math.abs(curT - lastSet) > 0.012) {
        try { video.currentTime = curT; lastSet = curT; } catch (e) {}
      }
      draw();
      // linger: 0 during the scrub, ramps 0->1 across the held tail (video parked,
      // text drifts down with the scroll).
      var linger = SCRUB_END < 1 ? Math.max(0, Math.min(1, (p - SCRUB_END) / (1 - SCRUB_END))) : 0;
      root.style.setProperty('--chero-p', p.toFixed(3));
      root.style.setProperty('--chero-t', t.toFixed(3));
      root.style.setProperty('--chero-linger', linger.toFixed(3));
      // cursor position (lerped, -0.5..0.5) drives a live 3D tilt of the text group
      root.style.setProperty('--chero-mx', mX.toFixed(3));
      root.style.setProperty('--chero-my', mY.toFixed(3));
      root.style.setProperty('--chero-cue', (1 - Math.min(1, p / 0.12)).toFixed(3));
      // Text reveal is DECOUPLED from the (slower) video scrub: it runs fast over an
      // early, fixed band of raw scroll so the copy snaps in while the car keeps
      // revealing slowly behind it.
      root.style.setProperty('--chero-reveal', Math.max(0, Math.min(1, (p - 0.10) / 0.14)).toFixed(3));
      requestAnimationFrame(tick);
    }

    function onMouse(e) {
      mTX = e.clientX / window.innerWidth - 0.5;
      mTY = e.clientY / window.innerHeight - 0.5;
    }
    function onResize() { resize(); updateHeader(); }

    window.addEventListener('scroll', updateHeader, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    prevListeners = { scroll: updateHeader, resize: onResize, mouse: onMouse };

    updateHeader();
    resize();
    requestAnimationFrame(tick);
  }

  // The theme editor re-renders sections on every setting change. Re-init on the
  // next tick so the hero (and its live water reflection) rebinds to fresh DOM.
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 60);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Back/forward cache restore (browser Back from a product/merch page). The page
  // is served frozen exactly as it was left. On real Chrome the hero's GPU-backed
  // canvas loses its decoded video frame on restore, and the scrub loop is dead,
  // so the opaque canvas paints pure black over the (now hidden) poster — a black
  // screen. Re-initialising in place is unreliable across browsers, so force a
  // clean reload: a fresh load always renders the hero correctly. The reload is a
  // normal navigation, so pageshow then fires with persisted=false (no loop).
  // [bfcache-reload-fix]
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) window.location.reload();
  });
})();
